import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Search,
  Pin,
  PinOff,
  Target,
  ShieldCheck,
  ServerCog,
  ListChecks,
  Folder,
  X as XIcon,
  Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  fetchPipelinePicker,
  pinPipeline as pinPipelineApi,
  unpinPipeline as unpinPipelineApi,
  PipelinePinLimitError,
  PickerCategory,
  PickerPipeline,
  PickerPinned,
  PickerTag,
  PipelinePickerPayload,
} from '../../services/smartStartApi';
import { fuzzyMatch } from '../../utils/fuzzyMatch';

interface Props {
  onPick: (pipelineKey: string, name: string) => void | Promise<void>;
}

const ICON_BY_NAME: Record<string, React.ElementType> = {
  target: Target,
  'shield-check': ShieldCheck,
  'server-cog': ServerCog,
  'list-checks': ListChecks,
  folder: Folder,
};

const COLOR_BY_RAMP: Record<string, { tile: string; icon: string; ring: string }> = {
  lavender: { tile: 'bg-lavender/10', icon: 'text-lavender', ring: 'ring-lavender/30' },
  coral: { tile: 'bg-rose-100', icon: 'text-rose-500', ring: 'ring-rose-200' },
  teal: { tile: 'bg-emerald-100', icon: 'text-emerald-600', ring: 'ring-emerald-200' },
  blue: { tile: 'bg-sky-100', icon: 'text-sky-600', ring: 'ring-sky-200' },
  gray: { tile: 'bg-gray-100', icon: 'text-gray-500', ring: 'ring-gray-200' },
};

const DEBOUNCE_MS = 90;

const PipelinePicker: React.FC<Props> = ({ onPick }) => {
  const [payload, setPayload] = useState<PipelinePickerPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());
  const [pinningKey, setPinningKey] = useState<string | null>(null);
  const [launchingKey, setLaunchingKey] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const handlePick = useCallback(async (pipelineKey: string, name: string) => {
    if (launchingKey) return;
    setLaunchingKey(pipelineKey);
    try {
      await onPick(pipelineKey, name);
    } finally {
      setLaunchingKey(null);
    }
  }, [launchingKey, onPick]);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchPipelinePicker();
      setPayload(data);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to load picker');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleTogglePin = async (pipelineKey: string, currentlyPinned: boolean) => {
    if (!payload) return;
    setPinningKey(pipelineKey);
    const previous = payload;
    try {
      if (currentlyPinned) {
        const nextPinned = previous.pinned.filter((p) => p.pipeline_key !== pipelineKey);
        setPayload({ ...previous, pinned: nextPinned });
        await unpinPipelineApi(pipelineKey);
      } else {
        if (previous.pinned.length >= previous.max_pins) {
          toast.error(`Maximum ${previous.max_pins} pinned pipelines. Unpin one first.`);
          return;
        }
        const found = findPipelineByKey(previous, pipelineKey);
        if (!found) return;
        const nextPinned: PickerPinned[] = [{ ...found.pipeline, category_slug: found.categorySlug }, ...previous.pinned];
        setPayload({ ...previous, pinned: nextPinned });
        await pinPipelineApi(pipelineKey);
      }
    } catch (err: any) {
      setPayload(previous);
      if (err instanceof PipelinePinLimitError) {
        toast.error(`Maximum ${err.limit} pinned pipelines.`);
      } else {
        toast.error(`Could not update pin: ${err?.response?.data?.error || err.message || err}`);
      }
    } finally {
      setPinningKey(null);
    }
  };

  const toggleTag = (slug: string) => {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const filteredCategories = useMemo<PickerCategory[]>(() => {
    if (!payload) return [];
    const tagFilter = activeTags;
    const q = debouncedSearch.trim();

    let allItems: { item: PickerPipeline; category: PickerCategory }[] = [];
    for (const cat of payload.categories) {
      for (const sub of cat.subcomponents) {
        for (const p of sub.pipelines) {
          allItems.push({ item: p, category: cat });
        }
      }
    }

    if (tagFilter.size > 0) {
      allItems = allItems.filter(({ item }) => {
        for (const tag of tagFilter) if (!item.tags.includes(tag)) return false;
        return true;
      });
    }

    let scoreMap: Map<number, number> | null = null;
    if (q) {
      const matched = fuzzyMatch(
        allItems,
        q,
        ({ item }) => [
          { weight: 4, value: item.name },
          { weight: 3, value: item.short_desc || '' },
          { weight: 2, value: (item.routing_keywords || []).join(' ') },
          { weight: 1, value: (item.use_cases || []).join(' ') },
          { weight: 2, value: (item.tags || []).join(' ') },
        ],
      );
      scoreMap = new Map(matched.map((m) => [m.item.item.id, m.score]));
      allItems = matched.map((m) => m.item);
    }

    const allowedIds = new Set(allItems.map(({ item }) => item.id));

    const out: PickerCategory[] = [];
    for (const cat of payload.categories) {
      const subs = [];
      for (const sub of cat.subcomponents) {
        const pipelines = sub.pipelines.filter((p) => allowedIds.has(p.id));
        if (q && scoreMap) {
          pipelines.sort((a, b) => (scoreMap!.get(b.id) || 0) - (scoreMap!.get(a.id) || 0));
        }
        if (pipelines.length === 0) continue;
        subs.push({ ...sub, pipelines });
      }
      if (subs.length === 0) continue;
      out.push({ ...cat, subcomponents: subs });
    }
    return out;
  }, [payload, activeTags, debouncedSearch]);

  const isFiltered = debouncedSearch.trim().length > 0 || activeTags.size > 0;
  const totalAfterFilter = filteredCategories.reduce(
    (acc, c) => acc + c.subcomponents.reduce((a, s) => a + s.pipelines.length, 0),
    0
  );

  const pinnedSet = useMemo(() => {
    if (!payload) return new Set<string>();
    return new Set(payload.pinned.map((p) => p.pipeline_key));
  }, [payload]);

  if (loading && !payload) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading pipelines…
      </div>
    );
  }

  if (error && !payload) {
    return (
      <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-900">
        Could not load pipelines: {error}
      </div>
    );
  }

  if (!payload) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500 font-bold">Or pick a known pipeline directly</div>
          <p className="text-sm text-gray-500 mt-0.5">Browse by category, or search to jump straight to one</p>
        </div>
        <div className="text-xs text-gray-400 whitespace-nowrap mt-0.5">
          {payload.pipeline_count} pipeline{payload.pipeline_count === 1 ? '' : 's'}
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search pipelines… try "compliance" or "CMDB"'
          className="w-full pl-9 pr-12 py-2.5 text-sm rounded-lg border border-gray-200 bg-gray-50 text-gray-700 placeholder:text-gray-400 focus:bg-white focus:border-lavender focus:ring-2 focus:ring-lavender/20 focus:outline-none"
          aria-label="Search pipelines"
        />
        <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-gray-500 bg-white border border-gray-200 rounded px-1.5 py-0.5">
          ⌘K
        </kbd>
      </div>

      {payload.pinned.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center gap-1.5 mb-2 text-xs uppercase tracking-wide text-gray-500 font-bold">
            <Pin className="w-3.5 h-3.5 text-lavender" />
            Pinned
          </div>
          <div className="flex flex-wrap gap-2">
            {payload.pinned.map((p) => (
              <PinnedPill
                key={p.pipeline_key}
                pipeline={p}
                onPick={() => handlePick(p.pipeline_key, p.name)}
                onUnpin={() => handleTogglePin(p.pipeline_key, true)}
                busy={pinningKey === p.pipeline_key}
                launching={launchingKey === p.pipeline_key}
                anyLaunching={launchingKey != null && launchingKey !== p.pipeline_key}
              />
            ))}
          </div>
        </div>
      )}

      {filteredCategories.length === 0 ? (
        <PickerEmptyState onClearFilters={() => { setSearch(''); setActiveTags(new Set()); }} hasFilters={isFiltered} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredCategories.map((cat) => (
            <CategoryCard
              key={cat.slug}
              category={cat}
              pinnedSet={pinnedSet}
              pinningKey={pinningKey}
              launchingKey={launchingKey}
              onPick={handlePick}
              onTogglePin={handleTogglePin}
            />
          ))}
        </div>
      )}

      {payload.tags.length > 0 && (
        <div className="mt-6 pt-5 border-t border-gray-100">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wide text-gray-500 font-semibold mr-1">Filter by tag</span>
            {payload.tags.map((tag) => (
              <TagChip
                key={tag.slug}
                tag={tag}
                active={activeTags.has(tag.slug)}
                onToggle={() => toggleTag(tag.slug)}
              />
            ))}
            {activeTags.size > 0 && (
              <button
                type="button"
                onClick={() => setActiveTags(new Set())}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 ml-1"
              >
                <XIcon className="w-3 h-3" />
                Clear
              </button>
            )}
          </div>
          {isFiltered && (
            <div className="text-center text-xs text-gray-500 mt-2">
              {totalAfterFilter} match{totalAfterFilter === 1 ? '' : 'es'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface CategoryCardProps {
  category: PickerCategory;
  pinnedSet: Set<string>;
  pinningKey: string | null;
  launchingKey: string | null;
  onPick: (pipelineKey: string, name: string) => void;
  onTogglePin: (key: string, currentlyPinned: boolean) => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, pinnedSet, pinningKey, launchingKey, onPick, onTogglePin }) => {
  const Icon = ICON_BY_NAME[category.icon_name] || Folder;
  const colors = COLOR_BY_RAMP[category.color_ramp] || COLOR_BY_RAMP.lavender;
  return (
    <div className="rounded-xl border border-gray-200 p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className={`p-2 rounded-lg ${colors.tile}`}>
          <Icon className={`w-5 h-5 ${colors.icon}`} />
        </div>
        <div className="min-w-0">
          <div className="text-base font-bold text-gray-900 leading-tight">{category.display_name}</div>
          <div className="text-xs text-gray-500 mt-0.5">{category.short_desc}</div>
        </div>
      </div>
      <div className="space-y-3">
        {category.subcomponents.map((sub) => (
          <div key={sub.label}>
            <div className="text-[10px] uppercase tracking-[0.08em] text-gray-500 font-bold mb-1.5">{sub.label}</div>
            <div className="flex flex-wrap gap-1.5">
              {sub.pipelines.map((p) => (
                <PipelinePill
                  key={p.pipeline_key}
                  pipeline={p}
                  pinned={pinnedSet.has(p.pipeline_key)}
                  busy={pinningKey === p.pipeline_key}
                  launching={launchingKey === p.pipeline_key}
                  anyLaunching={launchingKey != null && launchingKey !== p.pipeline_key}
                  onPick={() => onPick(p.pipeline_key, p.name)}
                  onTogglePin={() => onTogglePin(p.pipeline_key, pinnedSet.has(p.pipeline_key))}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface PipelinePillProps {
  pipeline: PickerPipeline;
  pinned: boolean;
  busy: boolean;
  launching: boolean;
  anyLaunching: boolean;
  onPick: () => void;
  onTogglePin: () => void;
}

const PipelinePill: React.FC<PipelinePillProps> = ({ pipeline, pinned, busy, launching, anyLaunching, onPick, onTogglePin }) => {
  const disabled = launching || anyLaunching;
  return (
    <div className="group relative inline-flex items-center">
      <button
        type="button"
        onClick={onPick}
        disabled={disabled}
        title={launching ? 'Launching…' : (pipeline.short_desc || pipeline.description || '')}
        className={`pl-3 ${launching ? 'pr-3' : 'pr-8'} py-1.5 text-sm rounded-full border transition max-w-[260px] truncate ${
          launching
            ? 'bg-lavender/15 text-lavender-deep border-lavender/40 cursor-wait'
            : disabled
              ? 'bg-gray-100 text-gray-400 border-transparent cursor-not-allowed opacity-60'
              : 'bg-gray-100 text-gray-700 border-transparent hover:bg-lavender/10 hover:text-lavender hover:border-lavender/30'
        }`}
      >
        {launching ? (
          <span className="inline-flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Wait — opening {pipeline.name}…
          </span>
        ) : pipeline.name}
      </button>
      {!launching && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onTogglePin(); }}
          disabled={busy || anyLaunching}
          aria-label={pinned ? `Unpin ${pipeline.name}` : `Pin ${pipeline.name}`}
          title={pinned ? 'Unpin' : 'Pin'}
          className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-full transition ${
            pinned
              ? 'opacity-100 text-lavender hover:bg-lavender/15'
              : 'opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-lavender hover:bg-lavender/10'
          } disabled:opacity-50`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : pinned ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
        </button>
      )}
    </div>
  );
};

interface PinnedPillProps {
  pipeline: PickerPinned;
  busy: boolean;
  launching: boolean;
  anyLaunching: boolean;
  onPick: () => void;
  onUnpin: () => void;
}

const PinnedPill: React.FC<PinnedPillProps> = ({ pipeline, busy, launching, anyLaunching, onPick, onUnpin }) => {
  const disabled = launching || anyLaunching;
  return (
    <div className="group relative inline-flex items-center">
      <button
        type="button"
        onClick={onPick}
        disabled={disabled}
        title={launching ? 'Launching…' : (pipeline.short_desc || pipeline.description || '')}
        className={`pl-3 ${launching ? 'pr-3' : 'pr-8'} py-1.5 text-sm rounded-full font-medium border transition max-w-[280px] truncate ${
          launching
            ? 'bg-lavender/20 text-lavender-deep border-lavender cursor-wait'
            : disabled
              ? 'bg-lavender-bg/40 text-lavender-deep/50 border-lavender-border/40 cursor-not-allowed'
              : 'bg-lavender-bg text-lavender-deep border-lavender-border hover:bg-lavender/15'
        }`}
      >
        {launching ? (
          <span className="inline-flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Wait — opening {pipeline.name}…
          </span>
        ) : pipeline.name}
      </button>
      {!launching && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onUnpin(); }}
          disabled={busy || anyLaunching}
          aria-label={`Unpin ${pipeline.name}`}
          title="Unpin"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-lavender-deep hover:bg-lavender/20 disabled:opacity-50 transition"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <PinOff className="w-3 h-3" />}
        </button>
      )}
    </div>
  );
};

interface TagChipProps {
  tag: PickerTag;
  active: boolean;
  onToggle: () => void;
}

const TagChip: React.FC<TagChipProps> = ({ tag, active, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-pressed={active}
    className={`px-3 py-1 text-xs rounded-full border transition ${
      active
        ? 'bg-lavender border-lavender text-white'
        : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200'
    }`}
  >
    {tag.display_name}
  </button>
);

interface EmptyStateProps {
  hasFilters: boolean;
  onClearFilters: () => void;
}

const PickerEmptyState: React.FC<EmptyStateProps> = ({ hasFilters, onClearFilters }) => (
  <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">
    {hasFilters ? (
      <>
        <p>No pipelines match the current filter.</p>
        <p className="mt-1">Try the natural language input above, or
          <button onClick={onClearFilters} className="text-lavender hover:text-lavender-deep ml-1 underline">
            clear filters
          </button>.
        </p>
      </>
    ) : (
      <p>No pipelines available yet.</p>
    )}
  </div>
);

function findPipelineByKey(payload: PipelinePickerPayload, key: string):
  | { pipeline: PickerPipeline; categorySlug: string }
  | null {
  for (const cat of payload.categories) {
    for (const sub of cat.subcomponents) {
      const found = sub.pipelines.find((p) => p.pipeline_key === key);
      if (found) return { pipeline: found, categorySlug: cat.slug };
    }
  }
  return null;
}

export default PipelinePicker;
